import { ThemePreviewTile } from './ThemePreviewTile';
import { landingThemes } from './themeData';

// Curated five-theme stack. Order is back-to-front; the last id sits on top.
// Light and dark backgrounds alternate so each sliver reads as a distinct
// sheet — three dark themes in a row would merge into one blob.
const STACK_ORDER = [
  'solarizedLight',
  'forest',
  'lavender',
  'monokai',
  'sakura',
];

const stackedThemes = STACK_ORDER.map(
  (id) => landingThemes.find((t) => t.id === id)!,
);

interface ThemeStackProps {
  /** Tailwind width class for the front card. Sized fixed so the absolutely
   *  positioned back cards inherit it via `inset-0`. */
  className?: string;
}

/**
 * Decorative fanned stack of theme preview tiles. Used inside the Community
 * section as a visual nod to the theme variety the app ships with.
 */
export function ThemeStack({ className = 'mx-auto w-[260px] sm:w-[300px]' }: ThemeStackProps) {
  const total = stackedThemes.length;
  const frontTheme = stackedThemes[total - 1];

  return (
    <div aria-hidden="true" className={`relative ${className}`}>
      {/* Invisible spacer: reserves natural width/height so the absolutely
          positioned cards have something to anchor to. */}
      <div className="invisible">
        <ThemePreviewTile theme={frontTheme} hideCaption compact />
      </div>
      {stackedThemes.map((theme, i) => {
        const depth = total - 1 - i;
        // Offset stays within the tile's 12px inner padding so back cards
        // peek as clean colored slivers, never as clipped sample text.
        const translateX = -depth * 11;
        const translateY = -depth * 11;
        const scale = 1 - depth * 0.015;
        return (
          <div
            key={theme.id}
            role="presentation"
            style={{
              transform: `translateX(${translateX}px) translateY(${translateY}px) scale(${scale})`,
              transformOrigin: '50% 100%',
              zIndex: 100 - depth,
            }}
            className="absolute inset-0"
          >
            <ThemePreviewTile theme={theme} hideCaption compact />
          </div>
        );
      })}
    </div>
  );
}

export default ThemeStack;
